/**
 * Max-hours balancing for the Datasets cart.
 *
 * When a cap forces trimming, the naive cut — best scores first, or first added
 * first — lets whichever term returned the most footage crowd out the others: a
 * "garage, parking" cart capped at 50h ships as almost all parking. The fix is a
 * greedy water-fill: repeatedly give the next video to whichever term-group has
 * the least footage so far, taking that group's best-scoring unused video that
 * still fits the remaining budget.
 *
 * Runs client-side over the cart, O(items × groups) worst case — instant at any
 * realistic cart size, no backend round-trip.
 *
 * Grouping: an item carries the individual search terms that matched it
 * (CartItem.terms); items added by plain filtering fall back to their addedVia
 * label ("filters"), so a filter-only cart degenerates to a single group and the
 * cap becomes a plain truncation — balancing is only meaningful across terms.
 * A video matched by several terms is a member of each group but can ship only
 * once; when picked, its duration credits every group that claims it, so the
 * groups it also satisfies don't over-fill on top of it.
 *
 * Customer consolidation is AGGRESSIVE and org-major: every candidate list is
 * ordered by the org's total cart footage (biggest bin first), and score only
 * ranks videos WITHIN an org. Everything in the cart already cleared the
 * search's quality floor and the tab's filters, so the floor is the quality
 * guarantee — ranking across orgs by a few hundredths of cosine similarity
 * bought nothing, while shipping from 3 customers instead of 40 is the whole
 * point of a delivery. Term balance is unaffected: packing only reorders
 * choices WITHIN a group's turn, and every group shares the same org ranking,
 * so all terms drain the same big orgs together.
 *
 * Ordering big-orgs-first is not enough on its own, though. Measured on a real
 * 1,382h / 40-category cart capped to 1,000h: 9 orgs carried 97% of it and
 * FORTY-THREE more split the last 3%, about 0.7h each. Those were not packing
 * failures — they were rare categories with nowhere else to go, dragging one
 * small customer each into the delivery. Org count is downstream of category
 * count.
 *
 * So orgs are ADMITTED rather than merely preferred:
 *
 *   1. Open the smallest set of orgs whose cart footage covers the target.
 *      That is the floor on how few customers could possibly supply it.
 *   2. Coverage pass, rarest category first: every category ships at least one
 *      video. It takes from an already-open org when it can, and only opens a
 *      new one when the category has nothing anywhere else — which is the only
 *      reason a 0.7h customer should ever enter a delivery.
 *   3. Water-fill the rest, restricted to open orgs. When every category is
 *      stuck and the target is still short, open the next-biggest org and
 *      resume — so the org list grows only as far as actually filling the
 *      order requires.
 *
 * The alternative was a hard cap on org count, which is simpler but drops
 * categories that exist only in small customers. Keeping every category alive
 * costs a handful of extra orgs and is the tradeoff the deliveries want.
 */
import { CartItem } from './whShared';

export interface BalanceGroup {
  term: string;
  /** Membership counts. A multi-term video counts in each of its groups, so
   *  these can sum past the distinct total — same convention as the search's
   *  own per-term tallies. */
  availVideos: number;
  availSeconds: number;
  pickedVideos: number;
  pickedSeconds: number;
}

export interface BalanceResult {
  /** What ships, in cart order — the whole cart when no trimming was needed. */
  selected: CartItem[];
  groups: BalanceGroup[];
  trimmed: boolean;
  totalSeconds: number;
  /** Orgs opened ONLY to keep a category from shipping zero — i.e. the ones a
   *  pure fewest-customers packing would not have touched. Surfaced so "why am
   *  I shipping from 15 customers and not 9" has an answer on screen. */
  coverageOrgs: string[];
}

/** The packer's last tiebreak. `rank` wins when set — a migration uses it to
 *  fold donor-side damage into what is otherwise a pure match score. */
const rankOf = (it: CartItem): number => it.rank ?? it.score ?? -1;

/** A donor category may never be cut below this many videos. Breadth is what a
 *  diversity bucket is FOR, so it is a floor rather than a preference. */
export const DONOR_KEEP = 3;

export interface DonorRule {
  /** Videos this migration must not take. */
  protectedFns: Set<string>;
  /** 0..1 per video: how deep the thinnest category it belongs to is, relative
   *  to the donor's deepest. Blended into `rank` so that, among videos it is
   *  allowed to take, the packer prefers the ones the donor can spare. */
  safety: Map<string, number>;
  /** category -> how many of its videos may leave. */
  allowance: Map<string, number>;
}

/**
 * Which of a donor bucket's videos may be migrated out of it.
 *
 * A per-category QUOTA, not a static "is this category thin" test: a category
 * of five with a floor of three can give up two, and the third-largest video in
 * it must stay eligible while the fifth does not. Testing thinness up front
 * instead lets a category of five be cut to one, because nothing tracks what
 * earlier picks already took.
 *
 * Within a category the longest videos are offered first: the target is hours,
 * so the fewest videos that carry them is the smallest disturbance to the
 * donor. A video belonging to several categories must clear the quota in all of
 * them — taking it spends one from each.
 */
export function donorAllowance(
  donorCats: { [fn: string]: string[] },
  durationOf: (fn: string) => number,
  keep: number = DONOR_KEEP,
): DonorRule {
  const byCat = new Map<string, string[]>();
  const counts = new Map<string, number>();
  Object.entries(donorCats).forEach(([fn, cats]) =>
    cats.forEach((c) => {
      (byCat.get(c) || byCat.set(c, []).get(c)!).push(fn);
      counts.set(c, (counts.get(c) || 0) + 1);
    }),
  );
  const allowance = new Map<string, number>();
  const allowedIn = new Map<string, Set<string>>();
  byCat.forEach((fns, cat) => {
    const n = Math.max(0, fns.length - keep);
    allowance.set(cat, n);
    allowedIn.set(
      cat,
      new Set(
        fns
          .slice()
          .sort((a, b) => durationOf(b) - durationOf(a) || a.localeCompare(b))
          .slice(0, n),
      ),
    );
  });
  const fattest = Math.max(1, ...Array.from(counts.values()));
  const protectedFns = new Set<string>();
  const safety = new Map<string, number>();
  Object.entries(donorCats).forEach(([fn, cats]) => {
    if (!cats.length) {
      safety.set(fn, 1); // uncategorised: nothing to protect
      return;
    }
    if (!cats.every((c) => allowedIn.get(c)!.has(fn))) protectedFns.add(fn);
    safety.set(fn, Math.min(...cats.map((c) => counts.get(c) || 0)) / fattest);
  });
  return { protectedFns, safety, allowance };
}

const termsOf = (it: CartItem): string[] =>
  it.terms && it.terms.length ? it.terms : [it.addedVia];

/** How short of the target it has to be before opening ANOTHER customer is
 *  worth it, as a fraction of the target. Without this the escalation in step 3
 *  keeps opening orgs to chase the last few minutes — on the 1,000h cart it
 *  pulled in two more customers for 0.05h between them, which is the exact
 *  behaviour this rewrite exists to kill. 1% of 1,000h is 10h: a real gap. */
const ESCALATE_FLOOR = 0.01;

export function balanceSelection(
  items: CartItem[],
  maxSeconds: number | null,
): BalanceResult {
  const total = items.reduce((a, it) => a + (it.duration || 0), 0);

  const groupStats = (selected: CartItem[]): BalanceGroup[] => {
    const g = new Map<string, BalanceGroup>();
    const stat = (term: string) => {
      let e = g.get(term);
      if (!e) {
        e = { term, availVideos: 0, availSeconds: 0, pickedVideos: 0, pickedSeconds: 0 };
        g.set(term, e);
      }
      return e;
    };
    items.forEach((it) =>
      termsOf(it).forEach((t) => {
        const e = stat(t);
        e.availVideos += 1;
        e.availSeconds += it.duration || 0;
      }),
    );
    const sel = new Set(selected.map((it) => it.filename));
    items.forEach((it) => {
      if (!sel.has(it.filename)) return;
      termsOf(it).forEach((t) => {
        const e = stat(t);
        e.pickedVideos += 1;
        e.pickedSeconds += it.duration || 0;
      });
    });
    return Array.from(g.values()).sort((a, b) => b.availSeconds - a.availSeconds);
  };

  if (maxSeconds == null || total <= maxSeconds) {
    return {
      selected: items,
      groups: groupStats(items),
      trimmed: false,
      totalSeconds: total,
      coverageOrgs: [],
    };
  }

  // Cart footage per org — the "bin sizes" consolidation packs toward.
  const orgAvail = new Map<string, number>();
  items.forEach((it) =>
    orgAvail.set(it.orgId, (orgAvail.get(it.orgId) || 0) + (it.duration || 0)),
  );

  // Per-group candidate lists, org-major: biggest org's videos first, best
  // score first within an org. Sort is stable, so items without a score
  // (filter-added) keep their cart order inside their org block.
  const lists = new Map<string, CartItem[]>();
  items.forEach((it) =>
    termsOf(it).forEach((t) => {
      const l = lists.get(t) || [];
      l.push(it);
      lists.set(t, l);
    }),
  );
  const state = Array.from(lists.entries(), ([term, l]) => ({
    term,
    list: l.slice().sort(
      (a, b) =>
        (orgAvail.get(b.orgId) || 0) - (orgAvail.get(a.orgId) || 0) ||
        a.orgId.localeCompare(b.orgId) ||
        rankOf(b) - rankOf(a),
    ),
    idx: 0,
    seconds: 0,
    done: false,
  }));

  const picked = new Set<string>();
  let remaining = maxSeconds;

  // Step 1: the fewest orgs that could supply the target at all. A lower bound,
  // not a plan -- term balance always makes the real fill less efficient than
  // this, which is what step 3's escalation is for.
  const orgsBySize = Array.from(orgAvail.entries()).sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  const open = new Set<string>();
  let covered = 0;
  for (const [org, secs] of orgsBySize) {
    if (covered >= maxSeconds) break;
    open.add(org);
    covered += secs;
  }

  const take = (it: CartItem) => {
    picked.add(it.filename);
    const d = it.duration || 0;
    remaining -= d;
    const mine = new Set(termsOf(it));
    state.forEach((x) => {
      if (mine.has(x.term)) x.seconds += d;
    });
  };

  // Step 2: coverage. Rarest category first, because if the budget runs out
  // mid-pass the categories that needed protecting are the scarce ones.
  const coverageOrgs: string[] = [];
  const byScarcity = state
    .slice()
    .sort(
      (a, b) =>
        a.list.reduce((n, it) => n + (it.duration || 0), 0) -
        b.list.reduce((n, it) => n + (it.duration || 0), 0),
    );
  // Hoisted out of the loop: it reads `remaining`, which every pick mutates.
  const fits = (it: CartItem) =>
    !picked.has(it.filename) && (it.duration || 0) <= remaining;
  for (const s of byScarcity) {
    // Already satisfied by a video drawn for an overlapping category.
    if (s.seconds > 0) continue;
    // Lists are org-major, so the first hit is from the biggest org that has
    // this category -- coverage prefers big customers too.
    let it = s.list.find((c) => open.has(c.orgId) && fits(c));
    if (!it) {
      const outside = s.list.find(fits);
      if (outside) {
        open.add(outside.orgId);
        coverageOrgs.push(outside.orgId);
        it = outside;
      }
    }
    if (it) take(it);
  }

  // Step 3: water-fill the remainder within the open orgs, opening one more
  // only when every category is stuck and the target is still short.
  for (;;) {
    const active = state.filter((s) => !s.done);
    if (!active.length) {
      // Everything is stuck. Open the next-biggest org and let the groups
      // re-scan for what it added; stop when nothing is left to open.
      const next = orgsBySize.find(([org]) => !open.has(org));
      if (!next || remaining <= maxSeconds * ESCALATE_FLOOR) break;
      open.add(next[0]);
      state.forEach((s) => {
        s.done = false;
        s.idx = 0; // `picked` still guards against taking anything twice
      });
      continue;
    }
    const s = active.reduce((m, x) => (x.seconds < m.seconds ? x : m));
    // The group's next unused video that fits, from an OPEN org -- org-major
    // order, so "next" means "from the biggest still-available org". One that
    // doesn't fit is skipped, not terminal: a shorter video deeper in the list
    // may still close the gap.
    let it: CartItem | null = null;
    while (s.idx < s.list.length) {
      const cand = s.list[s.idx];
      if (
        open.has(cand.orgId) &&
        !picked.has(cand.filename) &&
        (cand.duration || 0) <= remaining
      ) {
        it = cand;
        break;
      }
      s.idx += 1;
    }
    if (!it) {
      s.done = true;
      continue;
    }
    s.idx += 1;
    take(it);
  }

  const selected = items.filter((it) => picked.has(it.filename));
  const shippedOrgs = new Set(selected.map((it) => it.orgId));
  return {
    selected,
    groups: groupStats(selected),
    trimmed: true,
    totalSeconds: selected.reduce((a, it) => a + (it.duration || 0), 0),
    // Only the ones that actually survived into the selection.
    coverageOrgs: coverageOrgs.filter((o) => shippedOrgs.has(o)),
  };
}

/**
 * Co-collect: split one cart into TWO disjoint deliveries, each with its own
 * target, while opening as few organizations as possible ACROSS BOTH.
 *
 * The single-bucket packer above answers "fewest customers for one order". Run
 * twice it would admit orgs twice — each bucket would open its own minimal set,
 * and the union is not minimal (measured need: a cars bucket and an everything
 * bucket drawing from one fleet should share their big orgs, not each recruit
 * their own). So admission is JOINT here: one open-org set, sized against the
 * SUM of both targets, and both buckets water-fill inside it.
 *
 * Ownership: every term belongs to exactly one side (side A = the terms the
 * user picked; side B = the rest), and every video ships in at most one bucket.
 * A video whose terms span both sides is "contested" — it is held back within
 * each org until that side has no exclusive video to take, and otherwise goes
 * to whichever side is further from its target when its turn comes. That is
 * the whole tie-break: deficit-driven, no lookahead, O(items × groups).
 *
 *   1. Open the smallest set of orgs whose cart footage covers targetA+targetB.
 *   2. Coverage pass per side, rarest category first (same rule as above) —
 *      every category on both sides ships at least one video.
 *   3. Water-fill: at each step, the side with the larger RELATIVE deficit
 *      picks next (so a 1,000h bucket and a 200h bucket converge together),
 *      inside the side the least-filled category picks, org-major as always.
 *      When both sides stall and either is still short by more than
 *      ESCALATE_FLOOR of its target, open the next-biggest org and rescan.
 *
 * Best-effort by construction: a side whose footage runs out just lands short
 * (the "bit of everything" side has no minimum), and its categories stay as
 * even as the water-fill can make them — diversity is the balance rule itself.
 */
export interface DualBalanceResult {
  a: BalanceResult;
  b: BalanceResult;
  /** Every org that ships in either bucket — the number co-collect minimizes. */
  orgs: string[];
}

export function balanceDualSelection(
  items: CartItem[],
  termsA: Set<string>,
  maxA: number,
  maxB: number,
  /** Orgs that are already inside the destination buckets. They are open from
   *  the start: a top-up cannot "save" a customer the bucket already ships
   *  from, so drawing from them first is free — new orgs are only admitted
   *  once the seeded ones cannot cover the targets. */
  seedOrgs: Set<string> = new Set(),
): DualBalanceResult {
  type Side = 'a' | 'b';
  const sideOfTerm = (t: string): Side => (termsA.has(t) ? 'a' : 'b');
  const sidesOf = (it: CartItem): Set<Side> =>
    new Set(termsOf(it).map(sideOfTerm));

  const orgAvail = new Map<string, number>();
  items.forEach((it) =>
    orgAvail.set(it.orgId, (orgAvail.get(it.orgId) || 0) + (it.duration || 0)),
  );
  const orgsBySize = Array.from(orgAvail.entries()).sort(
    (x, y) => y[1] - x[1] || x[0].localeCompare(y[0]),
  );

  // Step 1: joint admission against the combined target. Each video counts
  // once — it can serve either side, but only one. Seeded orgs (already in
  // the destination buckets) come first and only ones with cart footage are
  // kept — an org with nothing to give would just confuse escalation.
  const open = new Set<string>();
  let covered = 0;
  seedOrgs.forEach((org) => {
    const secs = orgAvail.get(org);
    if (secs !== undefined) {
      open.add(org);
      covered += secs;
    }
  });
  for (const [org, secs] of orgsBySize) {
    if (covered >= maxA + maxB) break;
    if (open.has(org)) continue;
    open.add(org);
    covered += secs;
  }

  interface Group {
    term: string;
    side: Side;
    list: CartItem[];
    idx: number;
    seconds: number;
    done: boolean;
  }
  const groups = new Map<string, Group>();
  items.forEach((it) =>
    termsOf(it).forEach((t) => {
      let g = groups.get(t);
      if (!g) {
        g = { term: t, side: sideOfTerm(t), list: [], idx: 0, seconds: 0, done: false };
        groups.set(t, g);
      }
      g.list.push(it);
    }),
  );
  // Org-major like the single packer; within an org, videos exclusive to this
  // group's side come before contested ones (spend what only you can use
  // first), then best score.
  groups.forEach((g) =>
    g.list.sort(
      (x, y) =>
        (orgAvail.get(y.orgId) || 0) - (orgAvail.get(x.orgId) || 0) ||
        x.orgId.localeCompare(y.orgId) ||
        (sidesOf(x).size - sidesOf(y).size) ||
        rankOf(y) - rankOf(x),
    ),
  );

  const sides: Record<Side, { rem: number; max: number; groups: Group[] }> = {
    a: { rem: maxA, max: Math.max(maxA, 1), groups: [] },
    b: { rem: maxB, max: Math.max(maxB, 1), groups: [] },
  };
  groups.forEach((g) => sides[g.side].groups.push(g));

  const picked = new Map<string, Side>();
  const take = (it: CartItem, side: Side) => {
    picked.set(it.filename, side);
    const d = it.duration || 0;
    sides[side].rem -= d;
    const mine = new Set(termsOf(it).filter((t) => sideOfTerm(t) === side));
    sides[side].groups.forEach((g) => {
      if (mine.has(g.term)) g.seconds += d;
    });
  };

  // Step 2: coverage, per side, rarest first.
  const coverageOrgs: string[] = [];
  (['a', 'b'] as Side[]).forEach((side) => {
    const sd = sides[side];
    const byScarcity = sd.groups
      .slice()
      .sort(
        (x, y) =>
          x.list.reduce((n, it) => n + (it.duration || 0), 0) -
          y.list.reduce((n, it) => n + (it.duration || 0), 0),
      );
    const fits = (it: CartItem) =>
      !picked.has(it.filename) && (it.duration || 0) <= sd.rem;
    for (const g of byScarcity) {
      if (g.seconds > 0) continue;
      let it = g.list.find((c) => open.has(c.orgId) && fits(c));
      if (!it) {
        const outside = g.list.find(fits);
        if (outside) {
          open.add(outside.orgId);
          coverageOrgs.push(outside.orgId);
          it = outside;
        }
      }
      if (it) take(it, side);
    }
  });

  // Step 3: deficit-driven joint water-fill.
  for (;;) {
    const live = (['a', 'b'] as Side[]).filter((s) => {
      const sd = sides[s];
      return sd.rem > sd.max * ESCALATE_FLOOR && sd.groups.some((g) => !g.done);
    });
    if (!live.length) {
      const short = (['a', 'b'] as Side[]).some(
        (s) => sides[s].rem > sides[s].max * ESCALATE_FLOOR,
      );
      const next = orgsBySize.find(([org]) => !open.has(org));
      if (!short || !next) break;
      open.add(next[0]);
      groups.forEach((g) => {
        g.done = false;
        g.idx = 0; // `picked` still guards against taking anything twice
      });
      continue;
    }
    const side = live.reduce((m, s) =>
      sides[s].rem / sides[s].max > sides[m].rem / sides[m].max ? s : m,
    );
    const sd = sides[side];
    const active = sd.groups.filter((g) => !g.done);
    const g = active.reduce((m, x) => (x.seconds < m.seconds ? x : m));
    let it: CartItem | null = null;
    while (g.idx < g.list.length) {
      const cand = g.list[g.idx];
      if (
        open.has(cand.orgId) &&
        !picked.has(cand.filename) &&
        (cand.duration || 0) <= sd.rem
      ) {
        it = cand;
        break;
      }
      g.idx += 1;
    }
    if (!it) {
      g.done = true;
      continue;
    }
    g.idx += 1;
    take(it, side);
  }

  // Per-side results, in the single packer's shape so everything downstream
  // (mix panels, payload building) reads them the same way.
  const result = (side: Side): BalanceResult => {
    const selected = items.filter((it) => picked.get(it.filename) === side);
    const shippedOrgs = new Set(selected.map((it) => it.orgId));
    const sideItems = items.filter((it) =>
      termsOf(it).some((t) => sideOfTerm(t) === side),
    );
    const g = new Map<string, BalanceGroup>();
    const stat = (term: string) => {
      let e = g.get(term);
      if (!e) {
        e = { term, availVideos: 0, availSeconds: 0, pickedVideos: 0, pickedSeconds: 0 };
        g.set(term, e);
      }
      return e;
    };
    sideItems.forEach((it) =>
      termsOf(it)
        .filter((t) => sideOfTerm(t) === side)
        .forEach((t) => {
          const e = stat(t);
          e.availVideos += 1;
          e.availSeconds += it.duration || 0;
        }),
    );
    selected.forEach((it) =>
      termsOf(it)
        .filter((t) => sideOfTerm(t) === side)
        .forEach((t) => {
          const e = stat(t);
          e.pickedVideos += 1;
          e.pickedSeconds += it.duration || 0;
        }),
    );
    return {
      selected,
      groups: Array.from(g.values()).sort((x, y) => y.availSeconds - x.availSeconds),
      trimmed: true,
      totalSeconds: selected.reduce((n, it) => n + (it.duration || 0), 0),
      coverageOrgs: coverageOrgs.filter((o) => shippedOrgs.has(o)),
    };
  };

  const a = result('a');
  const b = result('b');
  return {
    a,
    b,
    orgs: Array.from(
      new Set([...a.selected, ...b.selected].map((it) => it.orgId)),
    ),
  };
}
